import { create } from "zustand";
import type { SeatStatus } from "@synapse/engine";

import { ariadneSeat } from "../ariadne/seat";
import { gameController } from "../game/services/GameController";
import { gameActionsService } from "../game/services/GameActionsService";
import { useGameplayStore } from "../game/stores/useGameplayStore";
import { useGameFlowStore } from "../game/stores/useGameFlowStore";
import { unifiedDataStore } from "../services/UnifiedDataStore";
import { bestRoutePath } from "../utils/graphUtils";
import type { DailyChallenge } from "../types/dailyChallenges";
import type { PathDisplayMode } from "../game/gameLogic";
import type {
  MoveJudgement,
  PotentialRarestMove,
  BacktrackReportEntry,
  GameReport,
} from "../utils/gameReportUtils";
import { useDailyChallengeStore } from "./useDailyChallengeStore";
import { useAssetStore } from "./useAssetStore";

// Export GameState type for backward compatibility
export type GameState = GameStoreFacade;

/**
 * PROPER Game Store Facade
 *
 * This provides the EXACT same interface as the old useGameStore
 * while delegating everything to the new architecture.
 *
 * NO MORE DUPLICATION - Single source of truth through delegation.
 */
export interface GameStoreFacade {
  // Core actions that components need
  loadInitialData: () => Promise<boolean>;
  startGame: () => Promise<void>;
  startChallengeGame: (
    startWord: string,
    targetWord: string,
    dailyChallenge?: DailyChallenge,
  ) => Promise<void>;
  startDailyChallengeGame: () => Promise<void>;
  selectWord: (word: string) => void;
  giveUp: () => void;
  backtrackToWord: (word: string, index: number) => void;
  testWordCollectionsForDate: (month: number, day: number) => Promise<void>;

  // EXPOSE STATE DIRECTLY (like old store) - computed from new stores
  // Gameplay Store Properties
  gameStatus: "idle" | "loading" | "playing" | "won" | "given_up";
  startWord: string | null;
  targetWord: string | null;
  currentWord: string | null;
  playerPath: string[];
  /** The gold path, fixed at game start (persisted key; Ruling R6). */
  optimalPath: string[];
  /** The Local Optimal route from the current word (persisted key; Ruling R6). */
  suggestedPathFromCurrent: string[];
  aiPath: string[];
  aiModel: string | null;
  ariadneStatus: SeatStatus | null;
  pathDisplayMode: PathDisplayMode;
  optimalChoices: MoveJudgement[];
  backtrackHistory: BacktrackReportEntry[];
  potentialRarestMovesThisGame: PotentialRarestMove[];
  isDailyChallenge: boolean;
  isChallenge: boolean;
  currentDailyChallengeId?: string;
  gameReport: GameReport | null;

  // Flow Store Properties
  isLoadingData: boolean;
  errorLoadingData: string | null;
  initialDataLoaded: boolean;
  wordCollections: unknown[]; // WordCollectionWithStatus[]
  hasPendingChallenge: boolean;
  pendingChallengeWords: { startWord: string; targetWord: string } | null;
  globalMessageVisible: boolean;
  globalMessage: string;

  // State getters (computed from other stores)
  getGameplayState: () => ReturnType<typeof useGameplayStore.getState>;
  getFlowState: () => ReturnType<typeof useGameFlowStore.getState>;

  // Legacy compatibility methods
  setPathDisplayMode: (mode: Partial<PathDisplayMode>) => void;
  setHasPendingChallenge: (hasPending: boolean) => void;
  setPendingChallengeWords: (
    words: { startWord: string; targetWord: string } | null,
  ) => void;
  showGlobalMessage: (message: string) => void;
  hideGlobalMessage: () => void;
  reset: () => void;
}

export const useGameStore = create<GameStoreFacade>((set, _get) => {
  // Subscribe to underlying stores to keep facade state synchronized
  const syncState = () => {
    const gameplayState = useGameplayStore.getState();
    const flowState = useGameFlowStore.getState();

    set({
      // Sync gameplay store properties
      gameStatus: gameplayState.gameStatus,
      startWord: gameplayState.startWord,
      targetWord: gameplayState.targetWord,
      currentWord: gameplayState.currentWord,
      playerPath: gameplayState.playerPath,
      optimalPath: gameplayState.optimalPath,
      suggestedPathFromCurrent: gameplayState.suggestedPathFromCurrent,
      aiPath: gameplayState.aiPath,
      aiModel: gameplayState.aiModel,
      ariadneStatus: gameplayState.ariadneStatus,
      pathDisplayMode: gameplayState.pathDisplayMode,
      optimalChoices: gameplayState.optimalChoices,
      backtrackHistory: gameplayState.backtrackHistory,
      potentialRarestMovesThisGame: gameplayState.potentialRarestMovesThisGame,
      isDailyChallenge: gameplayState.isDailyChallenge,
      isChallenge: gameplayState.isChallenge,
      currentDailyChallengeId: gameplayState.currentDailyChallengeId,
      gameReport: gameplayState.gameReport,

      // Sync flow store properties
      isLoadingData: flowState.isLoadingData,
      errorLoadingData: flowState.errorLoadingData,
      initialDataLoaded: flowState.initialDataLoaded,
      wordCollections: flowState.wordCollections,
      hasPendingChallenge: flowState.hasPendingChallenge,
      pendingChallengeWords: flowState.pendingChallengeWords,
      globalMessageVisible: flowState.globalMessageVisible,
      globalMessage: flowState.globalMessage,
    });
  };

  // Subscribe to both stores
  const _unsubGameplay = useGameplayStore.subscribe(syncState);
  const _unsubFlow = useGameFlowStore.subscribe(syncState);

  // Initialize with current state
  syncState();

  return {
    // Initial state - will be overwritten by syncState
    gameStatus: "idle",
    startWord: null,
    targetWord: null,
    currentWord: null,
    playerPath: [],
    optimalPath: [],
    suggestedPathFromCurrent: [],
    aiPath: [],
    aiModel: null,
    ariadneStatus: null,
    pathDisplayMode: {
      player: true,
      optimal: false,
      suggested: false,
      ai: false,
    },
    optimalChoices: [] as MoveJudgement[],
    backtrackHistory: [] as BacktrackReportEntry[],
    potentialRarestMovesThisGame: [] as PotentialRarestMove[],
    isDailyChallenge: false,
    isChallenge: false,
    gameReport: null,
    isLoadingData: false,
    errorLoadingData: null,
    initialDataLoaded: false,
    wordCollections: [],
    hasPendingChallenge: false,
    pendingChallengeWords: null,
    globalMessageVisible: false,
    globalMessage: "",

    // Delegate core actions to services
    loadInitialData: () => gameController.loadInitialData(),
    startGame: async () => {
      await gameController.startRandomGame();
      // Force facade sync to ensure immediate state consistency for tests
      syncState();
    },
    startChallengeGame: async (startWord, targetWord, dailyChallenge) => {
      const gameplayStore = useGameplayStore.getState();
      const _gameFlowStore = useGameFlowStore.getState();
      const assetStore = useAssetStore.getState();

      // Clear any current game state
      gameplayStore.resetGameState();
      ariadneSeat.abandon();

      // Validate that words exist in graph data
      const graphData = assetStore.graphData;
      if (!graphData || !graphData[startWord] || !graphData[targetWord]) {
        // Words don't exist in graph - fail gracefully and stay idle
        console.warn("Challenge game words not found in graph data:", {
          startWord,
          targetWord,
        });
        return;
      }

      // The gold path, fixed at game start
      const goldPath = bestRoutePath(graphData, startWord, targetWord);

      // Set up the challenge game only if validation passes
      gameplayStore.setGameState({
        gameStatus: "playing",
        startWord,
        targetWord,
        currentWord: startWord,
        playerPath: [startWord],
        isChallenge: true,
        isDailyChallenge: !!dailyChallenge,
        isTutorial: false,
        currentDailyChallengeId: dailyChallenge?.id,
        optimalPath: goldPath,
        suggestedPathFromCurrent: goldPath,
        aiPath: [],
        aiModel: null,
        ariadneStatus: null,
      });

      // Game state is automatically managed by the stores
    },
    startDailyChallengeGame: async () => {
      const dailyChallengeStore = useDailyChallengeStore.getState();
      const assetStore = useAssetStore.getState();
      const gameplayStore = useGameplayStore;

      const todaysChallenge = dailyChallengeStore.todaysChallenge;
      if (!todaysChallenge) {
        console.warn("No daily challenge available");
        return;
      }

      const { graphData } = assetStore;
      if (
        !graphData ||
        !graphData[todaysChallenge.startWord] ||
        !graphData[todaysChallenge.targetWord]
      ) {
        console.warn("Challenge words not found in graph data");
        return;
      }

      // The gold path, fixed at game start
      const goldPath = bestRoutePath(
        graphData,
        todaysChallenge.startWord,
        todaysChallenge.targetWord,
      );

      // Set up daily challenge game state
      ariadneSeat.abandon();
      gameplayStore.getState().setGameState({
        gameStatus: "playing",
        startWord: todaysChallenge.startWord,
        targetWord: todaysChallenge.targetWord,
        currentWord: todaysChallenge.startWord,
        playerPath: [todaysChallenge.startWord],
        optimalPath: goldPath,
        suggestedPathFromCurrent: goldPath,
        isChallenge: false,
        isDailyChallenge: true,
        // Explicitly clear the tutorial flag: this path uses setGameState WITHOUT
        // a preceding resetGameState(), so a leftover isTutorial=true from the
        // just-finished tutorial game would otherwise mislabel the daily as the
        // tutorial (mode badge, telemetry, Q.E.D.).
        isTutorial: false,
        currentDailyChallengeId: todaysChallenge.id,
        aiPath: [],
        aiModel: null,
        ariadneStatus: null,
        optimalChoices: [] as MoveJudgement[],
        backtrackHistory: [] as BacktrackReportEntry[],
        potentialRarestMovesThisGame: [] as PotentialRarestMove[],
        moveHistory: [],
      });

      // Save the challenge game state
      const persistentState = {
        startWord: todaysChallenge.startWord,
        targetWord: todaysChallenge.targetWord,
        currentWord: todaysChallenge.startWord,
        playerPath: [todaysChallenge.startWord],
        optimalPath: goldPath,
        suggestedPathFromCurrent: goldPath,
        gameStatus: "playing" as const,
        optimalChoices: [] as MoveJudgement[],
        backtrackHistory: [] as BacktrackReportEntry[],
        potentialRarestMovesThisGame: [] as PotentialRarestMove[],
        moveHistory: [],
        startTime: Date.now(),
        isChallenge: false,
        isDailyChallenge: true,
        currentDailyChallengeId: todaysChallenge.id,
        pathDisplayMode: gameplayStore.getState().pathDisplayMode,
      };

      await unifiedDataStore.saveCurrentGame(persistentState, "challenge");

      // Force facade sync to ensure immediate state consistency for tests
      syncState();
    },
    selectWord: (word) => gameActionsService.selectWord(word),
    giveUp: () => gameActionsService.giveUp(),
    backtrackToWord: (word, index) =>
      gameActionsService.backtrackToWord(word, index),
    testWordCollectionsForDate: (month, day) =>
      gameController.testWordCollectionsForDate(month, day),

    // State getters
    getGameplayState: () => useGameplayStore.getState(),
    getFlowState: () => useGameFlowStore.getState(),

    // Legacy compatibility - delegate to focused stores
    setPathDisplayMode: (mode) =>
      useGameplayStore.getState().setPathDisplayMode(mode),
    setHasPendingChallenge: (hasPending) =>
      useGameFlowStore.getState().setHasPendingChallenge(hasPending),
    setPendingChallengeWords: (words) =>
      useGameFlowStore.getState().setPendingChallengeWords(words),
    showGlobalMessage: (message) =>
      useGameFlowStore.getState().showGlobalMessage(message),
    hideGlobalMessage: () => useGameFlowStore.getState().hideGlobalMessage(),
    reset: () => gameController.reset(),
  };
});

// Helper hooks for easier access to specific state
export const useGameplayState = () => useGameplayStore();
export const useGameFlowState = () => useGameFlowStore();
